import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ToggleOff as InactiveIcon,
  ToggleOn as ActiveIcon
} from '@mui/icons-material';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  flexRender
} from '@tanstack/react-table';
import toast from 'react-hot-toast';
import { LoadingSpinner, ConfirmDialog } from '../components/common';
import {
  selectAccounts,
  selectAccountsLoading,
  fetchAccounts,
  createAccountThunk,
  updateAccountThunk,
  deleteAccountThunk
} from '../store/accountsSlice';

export default function ManageAccounts() {
  const dispatch = useDispatch();
  const accounts = useSelector(selectAccounts);
  const loading = useSelector(selectAccountsLoading);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [formData, setFormData] = useState({ name: '', sage: 'A' });

  useEffect(() => {
    dispatch(fetchAccounts()).catch(() => {
      toast.error('Failed to load accounts');
    });
  }, [dispatch]);

  const handleCreateOpen = () => {
    setFormData({ name: '', sage: 'A' });
    setCreateDialogOpen(true);
  };

  const handleCreateClose = () => {
    setCreateDialogOpen(false);
    setFormData({ name: '', sage: 'A' });
  };

  const handleCreateSubmit = async () => {
    if (!formData.name || !formData.sage) {
      toast.error('Please fill in all fields');
      return;
    }

    try {
      await dispatch(createAccountThunk(formData));
      toast.success('Account created successfully');
      handleCreateClose();
    } catch (error) {
      console.error('Create account error:', error);
      toast.error('Failed to create account');
    }
  };

  const handleEditOpen = useCallback((account) => {
    setSelectedAccount(account);
    setFormData({ name: account.name, sage: account.sage });
    setEditDialogOpen(true);
  }, []);

  const handleEditClose = () => {
    setEditDialogOpen(false);
    setSelectedAccount(null);
    setFormData({ name: '', sage: 'A' });
  };

  const handleEditSubmit = async () => {
    if (!formData.name || !formData.sage) {
      toast.error('Please fill in all fields');
      return;
    }

    try {
      await dispatch(updateAccountThunk(selectedAccount.id, formData));
      toast.success('Account updated successfully');
      handleEditClose();
    } catch (error) {
      console.error('Update account error:', error);
      toast.error('Failed to update account');
    }
  };

  const handleDeleteOpen = useCallback((account) => {
    setSelectedAccount(account);
    setDeleteDialogOpen(true);
  }, []);

  const handleDeleteClose = () => {
    setDeleteDialogOpen(false);
    setSelectedAccount(null);
  };

  const handleDeleteConfirm = async () => {
    try {
      await dispatch(deleteAccountThunk(selectedAccount.id));
      toast.success('Account deleted successfully');
      handleDeleteClose();
    } catch (error) {
      console.error('Delete account error:', error);
      toast.error('Failed to delete account');
    }
  };

  const handleToggleStatus = useCallback(async (account) => {
    try {
      // 0 = active, 1 = on stop
      const newStopStatus = account.stop === 0 ? 1 : 0;
      await dispatch(updateAccountThunk(account.id, { ...account, stop: newStopStatus }));
      toast.success(`Account ${newStopStatus === 0 ? 'activated' : 'put on stop'}`);
    } catch (error) {
      console.error('Toggle status error:', error);
      toast.error('Failed to update account status');
    }
  }, [dispatch]);

  const columns = useMemo(
    () => [
      {
        accessorKey: 'name',
        header: 'Account Name',
        size: 250
      },
      {
        accessorKey: 'sage',
        header: 'Sage Code',
        size: 120
      },
      {
        accessorKey: 'stop',
        header: 'Status',
        cell: ({ row }) => (
          row.original.stop === 0 ? (
            <Chip label="Active" color="success" size="small" />
          ) : (
            <Chip label="On Stop" color="default" size="small" />
          )
        ),
        size: 120
      },
      {
        id: 'actions',
        header: 'Actions',
        cell: ({ row }) => (
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <IconButton
              size="small"
              onClick={() => handleToggleStatus(row.original)}
              color={row.original.stop === 0 ? 'success' : 'default'}
              title={row.original.stop === 0 ? 'Put On Stop' : 'Activate Account'}
            >
              {row.original.stop === 0 ? <ActiveIcon fontSize="small" /> : <InactiveIcon fontSize="small" />}
            </IconButton>
            <IconButton
              size="small"
              onClick={() => handleEditOpen(row.original)}
              color="primary"
              title="Edit"
            >
              <EditIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => handleDeleteOpen(row.original)}
              color="error"
              title="Delete"
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>
        ),
        size: 150
      }
    ],
    [handleToggleStatus, handleEditOpen, handleDeleteOpen]
  );

  const table = useReactTable({
    data: accounts,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel()
  });

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">
          Manage Accounts
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreateOpen}
        >
          Create Account
        </Button>
      </Box>

      {accounts.length === 0 ? (
        <Paper elevation={1} sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="body1" color="text.secondary">
            No accounts found. Create your first account to get started.
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper} elevation={2}>
          <Table>
            <TableHead>
              {table.getHeaderGroups().map(headerGroup => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map(header => (
                    <TableCell
                      key={header.id}
                      sx={{ fontWeight: 'bold' }}
                    >
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableHead>
            <TableBody>
              {table.getRowModel().rows.map(row => (
                <TableRow key={row.id} hover>
                  {row.getVisibleCells().map(cell => (
                    <TableCell key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Create Dialog */}
      <Dialog open={createDialogOpen} onClose={handleCreateClose} maxWidth="sm" fullWidth>
        <DialogTitle>Create New Account</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Account Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label="Sage Code"
              value={formData.sage}
              onChange={(e) => setFormData({ ...formData, sage: e.target.value })}
              fullWidth
              required
              helperText="Sage account code (typically starts with 'A')"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCreateClose}>Cancel</Button>
          <Button onClick={handleCreateSubmit} variant="contained">
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onClose={handleEditClose} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Account</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Account Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label="Sage Code"
              value={formData.sage}
              onChange={(e) => setFormData({ ...formData, sage: e.target.value })}
              fullWidth
              required
              helperText="Sage account code (typically starts with 'A')"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleEditClose}>Cancel</Button>
          <Button onClick={handleEditSubmit} variant="contained">
            Update
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={deleteDialogOpen}
        onCancel={handleDeleteClose}
        onConfirm={handleDeleteConfirm}
        title="Delete Account"
        message={`Are you sure you want to delete account "${selectedAccount?.name}"? This action cannot be undone.`}
      />
    </Box>
  );
}
